import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { api, ApiError } from '@/lib/api';
import { ContractFilters } from './ContractFilters';
import { ContractsTable } from './ContractsTable';
import { SubmitContractDialog } from '@/pages/submit/SubmitContractDialog';
import type { ContractFilterState } from './ContractFilters';
import type {
  ContractListResponse, ContractStatsResponse,
  ContractSummaryRow, SortField, SortDirection,
} from './types';

const PAGE_SIZE = 50;
const EMPTY_FILTERS: ContractFilterState = { status: 'all', contractType: 'all', search: '' };

function buildQuery(filters: ContractFilterState, page: number, sortField: SortField, sortDir: SortDirection): string {
  const params = new URLSearchParams();
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.contractType !== 'all') params.set('contract_type', filters.contractType);
  if (filters.search) params.set('search', filters.search);
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String((page - 1) * PAGE_SIZE));
  params.set('sort', sortField);
  params.set('direction', sortDir);
  return `/api/v1/contracts?${params.toString()}`;
}

export function ContractsPage() {
  const { canReview } = useAuth();
  const canSubmit = canReview;

  const [contracts, setContracts] = useState<ContractSummaryRow[]>([]);
  const [stats, setStats] = useState<ContractStatsResponse | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<ContractFilterState>(EMPTY_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContracts = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const query = buildQuery(filters, page, sortField, sortDirection);
    Promise.all([
      api.get<ContractListResponse>(query),
      api.get<ContractStatsResponse>('/api/v1/contracts/stats'),
    ])
      .then(([list, s]) => {
        setContracts(list.data);
        setTotal(list.meta.total);
        setStats(s);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load contracts. Please try again.');
      })
      .finally(() => { setIsLoading(false); });
  }, [filters, page, sortField, sortDirection]);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  function handleFiltersChange(next: ContractFilterState) {
    setFilters(next);
    setPage(1);
  }

  function handleSort(field: SortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  }

  const noContracts = !isLoading && !error && total === 0 && filters.status === 'all' && !filters.search;
  const noResults = !isLoading && !error && contracts.length === 0 && !noContracts;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Contracts</h1>
        {canSubmit && <SubmitContractDialog />}
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label="Contract portfolio summary">
        {(['total', 'active', 'under_review', 'expiring_soon', 'at_risk'] as const).map((key) => {
          const labels: Record<string, string> = {
            total: 'Total Contracts', active: 'Active',
            under_review: 'Under Review', expiring_soon: 'Expiring Soon', at_risk: 'At Risk',
          };
          const value = stats?.[key] ?? null;
          return (
            <Card key={key} className={key === 'at_risk' ? 'border-orange-200 dark:border-orange-800' : ''}>
              <CardContent className="pt-4 pb-3 px-4">
                {isLoading || !stats ? (
                  <Skeleton className="h-7 w-12 mb-1" />
                ) : key === 'at_risk' ? (
                  <Link to="/dashboard" className="group" aria-label={`${value ?? 0} at-risk contracts — view portfolio risk`}>
                    <div className="text-2xl font-bold text-orange-600 group-hover:underline">
                      {value}
                    </div>
                    <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-orange-500" aria-hidden="true" />
                  </Link>
                ) : (
                  <div className="text-2xl font-bold">{value ?? '—'}</div>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">{labels[key]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ContractFilters filters={filters} onChange={handleFiltersChange} />

      {error && (
        <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm" role="alert">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <span className="text-destructive">{error}</span>
          <Button variant="ghost" size="sm" onClick={loadContracts} className="ml-auto">
            <RefreshCw className="h-4 w-4 mr-1" aria-hidden="true" />Retry
          </Button>
        </div>
      )}

      {noContracts && (
        <EmptyState
          icon={FileText}
          title="No contracts yet."
          description={canSubmit
            ? 'Submit a contract for review to get started.'
            : 'No contracts have been created yet.'}
          action={canSubmit ? <SubmitContractDialog label="Submit for Review" /> : undefined}
          className="mt-8"
        />
      )}

      {noResults && (
        <EmptyState
          icon={FileText}
          title="No contracts match your filters."
          description="Try adjusting the search or filters above."
          className="mt-8"
        />
      )}

      {!error && !noContracts && (
        <ContractsTable
          contracts={contracts}
          isLoading={isLoading}
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
